import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useSearchParams } from 'react-router-dom';
import { Users, Plus, PencilSimple, Trash, Student, CalendarBlank, Clock, UserCircle, MagnifyingGlass, X, NotePencil, Bell } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { AdultAcademyPanel } from '../components/AdultAcademyPanel';
import { toast } from 'sonner';

const AdminAcademy = () => {
  const { t, language, API, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const L = (en, de) => language === 'de' ? de : en;
  const weekdays = language === 'de'
    ? ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const [tab, setTab] = useState('juniors');
  const [juniors, setJuniors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [reminding, setReminding] = useState(false);
  const sendReminder = async (j) => {
    if (!window.confirm(L(`Send a payment reminder to ${j.parent_email}?`, `Zahlungserinnerung an ${j.parent_email} senden?`))) return;
    setReminding(true);
    try {
      await API.post(`/academy/juniors/${j.id}/payment-reminder`);
      toast.success(L('Reminder sent', 'Erinnerung gesendet'));
      setEditingJunior(prev => (prev && prev.id === j.id ? { ...prev, payment_reminder_sent_at: new Date().toISOString() } : prev));
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setReminding(false); }
  };

  // Junior modal
  const [showJuniorModal, setShowJuniorModal] = useState(false);
  const [editingJunior, setEditingJunior] = useState(null);
  const [jForm, setJForm] = useState({ name: '', birth_year: '', parent_name: '', parent_phone: '', parent_email: '', player_level: '5', membership_type: '', membership_start: '', membership_end: '', training_frequency: '', training_times: '', notes: '' });

  // Group modal
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [gForm, setGForm] = useState({ name: '', trainer_id: '', court_id: '', weekday: '0', start_time: '16:00', duration_minutes: '60', category: 'junior', junior_ids: [], member_ids: [], price_per_junior: '0', price_per_member: '0', notes: '', session_weeks: '6', session_start_date: '' });

  // Adult members pool (customers) for the adult group picker
  const [adultMembers, setAdultMembers] = useState([]);
  const [adultSearch, setAdultSearch] = useState('');

  const [confirmAction, setConfirmAction] = useState(null);

  // Materialize (create bookings)
  const [showMaterialize, setShowMaterialize] = useState(false);
  const [mStartDate, setMStartDate] = useState('2026-05-01');
  const [mEndDate, setMEndDate] = useState('2026-06-30');
  const [mPreview, setMPreview] = useState(null);
  const [mLoading, setMLoading] = useState(false);
  const [mOverflow, setMOverflow] = useState({}); // { "group_id:chunk_idx": trainer_id }
  const [mResult, setMResult] = useState(null);

  // Per-group "Buchen" dialog (single group materialize). Kept separate from
  // the top-level materialize dialog so admins can quickly re-book one group
  // without touching the others.
  const [bookGroupDialog, setBookGroupDialog] = useState(null); // { group, startDate, endDate, loading, preview }

  // Format a materialize response into a clean, categorized toast so the admin
  // always understands WHY a click didn't produce bookings (no free court,
  // missing trainer, conflict, all sessions already booked …).
  const surfaceMaterializeResult = (data) => {
    const created = data.created || 0;
    const skippedExisting = data.skipped_existing || 0;
    const skippedConflict = data.skipped_conflict || 0;
    const missingCourt = (data.missing_court || []).length;
    const missingTrainer = (data.missing_trainer || []).length;
    const errCount = (data.errors || []).length;

    if (created > 0) {
      const parts = [`✓ ${created} ${L('weekly bookings created', 'Wochenbuchungen erstellt')}`];
      if (skippedExisting > 0) parts.push(`${skippedExisting} ${L('already existed', 'bestanden bereits')}`);
      if (skippedConflict > 0) parts.push(`${skippedConflict} ${L('slot conflicts', 'Slot-Konflikte')}`);
      if (missingCourt > 0) parts.push(`${missingCourt} ${L('without free court', 'ohne freien Court')}`);
      if (missingTrainer > 0) parts.push(`${missingTrainer} ${L('need overflow trainer', 'Overflow-Trainer nötig')}`);
      toast.success(parts.join(' · '));
      return;
    }

    // Nothing was created — surface the most likely reason first.
    if (missingTrainer > 0) {
      toast.error(L(
        `No trainer assigned for ${missingTrainer} overflow chunk(s). Please assign one via the top-level "Create weekly bookings" dialog.`,
        `Keinem Overflow-Chunk (${missingTrainer}) wurde ein Trainer zugewiesen. Bitte über den Dialog "Wochenbuchungen erstellen" oben zuweisen.`
      ));
      return;
    }
    if (missingCourt > 0 && skippedConflict === 0 && skippedExisting === 0) {
      toast.error(L(
        `No free court available at this time for ${missingCourt} session(s). Free up a court or change the group's court/time and try again.`,
        `Kein Court zur gewählten Zeit frei (${missingCourt} Lektionen). Court freigeben oder Zeit/Court der Gruppe ändern und erneut versuchen.`
      ));
      return;
    }
    if (skippedConflict > 0 && missingCourt === 0 && skippedExisting === 0) {
      toast.error(L(
        `${skippedConflict} session(s) conflict with existing bookings on the same court. Free the court or change the group's time.`,
        `${skippedConflict} Lektion(en) kollidieren mit bestehenden Buchungen auf demselben Court. Court freigeben oder Uhrzeit anpassen.`
      ));
      return;
    }
    if (skippedExisting > 0 && missingCourt === 0 && skippedConflict === 0) {
      toast(L(
        `All ${skippedExisting} sessions already exist — nothing new to book in this range.`,
        `Alle ${skippedExisting} Lektionen bestehen bereits — nichts Neues zu buchen in diesem Zeitraum.`
      ), { icon: 'ℹ️' });
      return;
    }
    if (errCount > 0) {
      toast.error((data.errors || []).slice(0, 3).join(' · '));
      return;
    }
    // Total no-op — most likely the date range doesn't include the group's weekday.
    toast.error(L(
      'No sessions to create. Check that the date range covers the group\'s weekday and that the group has juniors assigned.',
      'Nichts zu buchen. Prüfe, ob der Datumsbereich den Wochentag der Gruppe abdeckt und ob Junioren zugewiesen sind.'
    ));
  };

  // Auto-Groups from junior training_times
  const [showAutoGroups, setShowAutoGroups] = useState(false);
  const [agPreview, setAgPreview] = useState(null);
  const [agLoading, setAgLoading] = useState(false);
  const [agDefaultTrainer, setAgDefaultTrainer] = useState('');
  const [agDefaultCourt, setAgDefaultCourt] = useState('');
  const [agDefaultPrice, setAgDefaultPrice] = useState('0');
  const [agResult, setAgResult] = useState(null);
  const [agByLevel, setAgByLevel] = useState(true);

  const loadAutoGroupsPreview = useCallback(async () => {
    try {
      setAgLoading(true);
      setAgResult(null);
      const { data } = await API.post('/academy/auto-groups/preview', { group_by_level: agByLevel });
      setAgPreview(data);
    } catch (e) { toast.error(L('Preview failed', 'Vorschau fehlgeschlagen')); }
    finally { setAgLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, agByLevel]);

  const openAutoGroups = () => {
    setShowAutoGroups(true);
    setAgResult(null);
    loadAutoGroupsPreview();
  };

  // Re-run preview whenever the level toggle changes (only when dialog is open)
  useEffect(() => {
    if (showAutoGroups) loadAutoGroupsPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agByLevel]);

  const runAutoGroups = async () => {
    try {
      setAgLoading(true);
      const { data } = await API.post('/academy/auto-groups', {
        default_trainer_id: agDefaultTrainer || null,
        default_court_id: agDefaultCourt || null,
        default_price_per_junior: parseFloat(agDefaultPrice) || 0,
        group_by_level: agByLevel,
      });
      setAgResult(data);
      toast.success(L(`${data.created} groups created, ${data.extended} extended`, `${data.created} Gruppen erstellt, ${data.extended} erweitert`));
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setAgLoading(false); }
  };

  const loadMaterializePreview = useCallback(async () => {
    try {
      setMLoading(true);
      setMResult(null);
      const { data } = await API.post('/academy/materialize/preview', { start_date: mStartDate, end_date: mEndDate });
      setMPreview(data);
      // Pre-seed overflow map with empty values
      const next = {};
      data.groups.forEach(g => g.chunks.forEach(c => {
        if (c.needs_trainer) {
          const key = `${g.group_id}:${c.chunk_index}`;
          next[key] = mOverflow[key] || '';
        }
      }));
      setMOverflow(next);
    } catch (e) { toast.error(L('Preview failed', 'Vorschau fehlgeschlagen')); }
    finally { setMLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, mStartDate, mEndDate]);

  const openMaterializeDialog = () => {
    setShowMaterialize(true);
    setMResult(null);
    loadMaterializePreview();
  };

  const runMaterialize = async () => {
    // Validate: every needs_trainer chunk has a trainer assigned
    const missing = Object.entries(mOverflow).filter(([, v]) => !v);
    if (missing.length > 0) {
      toast.error(L(`Please assign a trainer to all overflow groups (${missing.length} missing)`, `Bitte allen Overflow-Gruppen einen Trainer zuweisen (${missing.length} fehlt/fehlen)`));
      return;
    }
    try {
      setMLoading(true);
      const overflow_trainers = Object.entries(mOverflow).map(([key, trainer_id]) => {
        const [group_id, chunk_index] = key.split(':');
        return { group_id, chunk_index: parseInt(chunk_index), trainer_id };
      });
      const { data } = await API.post('/academy/materialize', { start_date: mStartDate, end_date: mEndDate, overflow_trainers });
      setMResult(data);
      surfaceMaterializeResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setMLoading(false); }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [jRes, gRes, tRes, cRes] = await Promise.all([
        API.get('/academy/juniors'),
        API.get('/academy/groups'),
        API.get('/trainers'),
        API.get('/courts')
      ]);
      setJuniors(jRes.data);
      setGroups(gRes.data);
      setTrainers(tRes.data);
      setCourts(cRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { loadData(); }, [loadData]);

  // Open junior profile when navigated here with ?junior=<id>
  useEffect(() => {
    const jid = searchParams.get('junior');
    if (jid && juniors.length > 0) {
      const target = juniors.find(j => j.id === jid);
      if (target) {
        setTab('juniors');
        openEditJunior(target);
        // Clear the query param so reloading the page doesn't re-open the modal
        searchParams.delete('junior');
        setSearchParams(searchParams, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [juniors, searchParams]);

  // Junior CRUD
  const resetJForm = () => setJForm({ name: '', birth_year: '', parent_name: '', parent_phone: '', parent_email: '', player_level: '5', membership_type: '', membership_start: '', membership_end: '', training_frequency: '', training_times: '', notes: '' });

  const openEditJunior = (j) => {
    setEditingJunior(j);
    // Historical data: an old bug aliased "notes" to "training_times" (both
    // fields ended up with the schedule string). Detect that legacy state and
    // treat "notes" as empty so admins can start using it as real coach notes.
    const scheduleLike = j.notes && /[0-9]{1,2}:[0-9]{2}/.test(j.notes);
    const isLegacyAlias = scheduleLike && (!j.training_times || j.notes === j.training_times);
    const trainingTimes = j.training_times || (isLegacyAlias ? j.notes : '') || '';
    const realNotes = isLegacyAlias ? '' : (j.notes || '');
    setJForm({ name: j.name, birth_year: j.birth_year?.toString() || '', parent_name: j.parent_name || '', parent_phone: j.parent_phone || '', parent_email: j.parent_email || '', player_level: (j.player_level || 5).toString(), membership_type: j.membership_type || '', membership_start: j.membership_start || '', membership_end: j.membership_end || '', training_frequency: j.training_frequency || '', training_times: trainingTimes, notes: realNotes, membership_paid: !!j.membership_paid, membership_paid_date: (j.membership_paid_at || '').slice(0, 10) });
    setShowJuniorModal(true);
  };

  const handleSaveJunior = async () => {
    try {
      const payload = { ...jForm, birth_year: jForm.birth_year ? parseInt(jForm.birth_year) : null, player_level: parseFloat(jForm.player_level) || 5, membership_type: jForm.membership_type || null, membership_start: jForm.membership_start || null, membership_end: jForm.membership_end || null, training_frequency: jForm.training_frequency || null, notes: jForm.notes || null, training_times: jForm.training_times || null };
      if (editingJunior) await API.put(`/academy/juniors/${editingJunior.id}`, payload);
      else await API.post('/academy/juniors', payload);
      toast.success(editingJunior ? L('Junior updated', 'Junior aktualisiert') : L('Junior added', 'Junior hinzugefügt'));
      setShowJuniorModal(false); setEditingJunior(null); resetJForm(); loadData();
    } catch (e) { toast.error(L('Error', 'Fehler')); }
  };

  const handleDeleteJunior = (j) => {
    setConfirmAction({ message: L(`Delete ${j.name}?`, `${j.name} löschen?`), onConfirm: async () => {
      try { await API.delete(`/academy/juniors/${j.id}`); toast.success(L('Deleted', 'Gelöscht')); loadData(); } catch (e) { toast.error(L('Error', 'Fehler')); }
      setConfirmAction(null);
    }});
  };

  // Group CRUD
  const resetGForm = (category = 'junior') => setGForm({ name: '', trainer_id: '', court_id: '', weekday: '0', start_time: '16:00', duration_minutes: '60', category, junior_ids: [], member_ids: [], price_per_junior: '0', price_per_member: '0', notes: '', session_weeks: '6', session_start_date: '' });

  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGForm({
      name: g.name,
      trainer_id: g.trainer_id || '',
      court_id: g.court_id || '',
      weekday: g.weekday.toString(),
      start_time: g.start_time,
      duration_minutes: g.duration_minutes.toString(),
      category: g.category || 'junior',
      junior_ids: g.junior_ids || [],
      member_ids: g.member_ids || [],
      price_per_junior: (g.price_per_junior ?? 0).toString(),
      price_per_member: (g.price_per_member ?? 0).toString(),
      notes: g.notes || ''
    });
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    try {
      const payload = {
        ...gForm,
        weekday: parseInt(gForm.weekday),
        duration_minutes: parseInt(gForm.duration_minutes),
        trainer_id: gForm.trainer_id || null,
        court_id: gForm.court_id || null,
        price_per_junior: parseFloat(gForm.price_per_junior) || 0,
        price_per_member: parseFloat(gForm.price_per_member) || 0,
        session_weeks: parseInt(gForm.session_weeks) || 0,
        session_start_date: gForm.session_start_date || null,
      };
      if (editingGroup) {
        await API.put(`/academy/groups/${editingGroup.id}`, payload);
        toast.success(L('Group updated', 'Gruppe aktualisiert'));
      } else {
        const { data } = await API.post('/academy/groups', payload);
        if (data?.auto_materialized) {
          const m = data.auto_materialized;
          if (m.created > 0) {
            toast.success(L(
              `Group created — ${m.created} weekly bookings reserved`,
              `Gruppe erstellt — ${m.created} Wochenbuchungen reserviert`
            ));
          } else if (m.skipped_conflict > 0) {
            toast.error(L(
              `Group created, but ${m.skipped_conflict} weekly slots conflict with existing bookings. Please free the court or adjust the time.`,
              `Gruppe erstellt, aber ${m.skipped_conflict} Wochenslots sind bereits belegt. Bitte Platz freigeben oder Zeit anpassen.`
            ));
          } else {
            toast.success(L('Group created', 'Gruppe erstellt'));
          }
        } else {
          toast.success(L('Group created', 'Gruppe erstellt'));
        }
      }
      setShowGroupModal(false); setEditingGroup(null); resetGForm(gForm.category); loadData();
    } catch (e) { toast.error(L('Error', 'Fehler')); }
  };

  const handleDeleteGroup = (g) => {
    setConfirmAction({ message: L(`Delete group "${g.name}"?`, `Gruppe "${g.name}" löschen?`), onConfirm: async () => {
      try { await API.delete(`/academy/groups/${g.id}`); toast.success(L('Deleted', 'Gelöscht')); loadData(); } catch (e) { toast.error(L('Error', 'Fehler')); }
      setConfirmAction(null);
    }});
  };

  const toggleJuniorInGroup = (juniorId) => {
    setGForm(f => ({ ...f, junior_ids: f.junior_ids.includes(juniorId) ? f.junior_ids.filter(id => id !== juniorId) : [...f.junior_ids, juniorId] }));
  };

  const toggleMemberInGroup = (memberId) => {
    setGForm(f => ({ ...f, member_ids: f.member_ids.includes(memberId) ? f.member_ids.filter(id => id !== memberId) : [...f.member_ids, memberId] }));
  };

  // Load customer pool when adult-group modal opens or search changes
  useEffect(() => {
    if (!showGroupModal || gForm.category !== 'adult') return;
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/academy/members-pool${adultSearch ? `?q=${encodeURIComponent(adultSearch)}` : ''}`);
        setAdultMembers(data || []);
      } catch (e) { setAdultMembers([]); }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGroupModal, gForm.category, adultSearch]);

  // Auto-fetch per-member price for adult sessions whenever session length /
  // start time / weekday changes. Respects manual overrides: if the user has
  // typed a custom price and it matches neither 6w nor 12w matrix values, we
  // leave it alone.
  const [autoAdultPrice, setAutoAdultPrice] = useState(null); // {price_per_member, tier}
  useEffect(() => {
    if (!showGroupModal || gForm.category !== 'adult' || editingGroup) {
      setAutoAdultPrice(null);
      return;
    }
    const wks = parseInt(gForm.session_weeks);
    if (!wks || (wks !== 6 && wks !== 12)) {
      setAutoAdultPrice(null);
      return;
    }
    const fetchPrice = async () => {
      try {
        const { data } = await API.get(
          `/pricing/adult-session?start_time=${gForm.start_time}&weekday=${parseInt(gForm.weekday)}&session_weeks=${wks}`
        );
        setAutoAdultPrice(data);
        // Only overwrite price_per_member when it's still the default "0" or matches a known matrix value.
        const current = parseFloat(gForm.price_per_member) || 0;
        const matrixValues = [699, 899, 349.5, 449.5];
        if (current === 0 || matrixValues.includes(current)) {
          setGForm(f => ({ ...f, price_per_member: String(data.price_per_member) }));
        }
      } catch {
        setAutoAdultPrice(null);
      }
    };
    fetchPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGroupModal, gForm.category, gForm.session_weeks, gForm.start_time, gForm.weekday, editingGroup]);

  const getMembershipBadge = (j) => {
    if (!j.membership_type) return { label: L('No membership', 'Kein Abo'), cls: 'bg-slate-100 text-slate-500' };
    const end = j.membership_end ? new Date(j.membership_end) : null;
    const expired = end && end < new Date();
    if (expired) return { label: L('Expired', 'Abgelaufen'), cls: 'bg-red-50 text-red-600' };
    const typeLabel = j.membership_type === 'full_year' ? L('Yearly', 'Jahresabo')
      : j.membership_type === 'three_month' ? L('3 months', '3-Monats-Abo')
      : j.membership_type === 'half_year' ? L('Half Year', 'Halbjahresabo')  // legacy
      : j.membership_type;
    return { label: typeLabel, cls: 'bg-emerald-50 text-emerald-700' };
  };

  const isActive = (j) => j.is_active !== false;
  const activeJuniors = juniors.filter(isActive);
  const isPaymentOpen = (j) => isActive(j) && !!j.membership_type && !j.membership_paid;
  const filteredJuniors = juniors.filter(j => (j.name?.toLowerCase().includes(search.toLowerCase()) || j.parent_name?.toLowerCase().includes(search.toLowerCase())) && (!openOnly || isPaymentOpen(j)))
    .sort((a, b) => Number(!isActive(a)) - Number(!isActive(b)));

  const toggleJuniorActive = (j) => {
    const deactivate = isActive(j);
    setConfirmAction({ label: deactivate ? L('Deactivate', 'Deaktivieren') : L('Activate', 'Aktivieren'), message: deactivate
      ? L(`Set ${j.name} inactive? The profile is kept, but removed from all groups and future lessons.`, `${j.name} auf inaktiv setzen? Das Profil bleibt, wird aber aus allen Gruppen und zukünftigen Lektionen entfernt.`)
      : L(`Reactivate ${j.name}?`, `${j.name} wieder aktivieren?`), onConfirm: async () => {
      try {
        const { data } = await API.put(`/academy/juniors/${j.id}/active`, { active: !deactivate });
        toast.success(deactivate
          ? L(`Inactive · removed from ${data.removed_groups} group(s), ${data.removed_lessons} lesson(s)`, `Inaktiv · aus ${data.removed_groups} Gruppe(n) und ${data.removed_lessons} Lektion(en) entfernt`)
          : L('Reactivated', 'Wieder aktiv'));
        loadData();
      } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
      setConfirmAction(null);
    }});
  };

  const getJuniorGroupCount = (juniorId) => groups.filter(g => g.junior_ids?.includes(juniorId)).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-academy">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{L('Academy', 'Akademie')}</h1>
          <p className="text-slate-500">{activeJuniors.length} {L('Juniors', 'Junioren')} · {groups.length} {L('Groups', 'Gruppen')}</p>
        </div>
      </div>

      {/* Tab Switch */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setTab('juniors')} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${tab === 'juniors' ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid="tab-juniors">
          <Users size={16} className="inline mr-1.5" />{L('Juniors', 'Junioren')} ({activeJuniors.length})
        </button>
        <button onClick={() => setTab('groups')} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${tab === 'groups' ? 'bg-blue-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid="tab-groups">
          <CalendarBlank size={16} className="inline mr-1.5" />{L('Junior Groups', 'Junior-Gruppen')} ({groups.filter(g => (g.category || 'junior') === 'junior').length})
        </button>
        <button onClick={() => setTab('adult')} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${tab === 'adult' ? 'bg-violet-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid="tab-adult-academy">
          <Users size={16} className="inline mr-1.5" />Adult Academy
        </button>
      </div>

      {tab === 'adult' && <AdultAcademyPanel API={API} L={L} />}

      {/* JUNIORS TAB */}
      {tab === 'juniors' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white border-slate-200" placeholder={L('Search juniors...', 'Junioren suchen...')} data-testid="search-juniors" />
            </div>
            <button type="button" onClick={() => setOpenOnly(v => !v)} data-testid="filter-payment-open"
              className={`px-4 py-2 rounded-lg text-sm font-bold border-2 transition-colors whitespace-nowrap ${openOnly ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
              {L('Payment open', 'Zahlung offen')} ({juniors.filter(isPaymentOpen).length})
            </button>
            <Button onClick={() => { resetJForm(); setEditingJunior(null); setShowJuniorModal(true); }} className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold gap-2" data-testid="add-junior-btn">
              <Plus size={16} weight="bold" /> {L('Add Junior', 'Junior hinzufügen')}
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredJuniors.map(j => {
              const badge = getMembershipBadge(j);
              const groupCount = getJuniorGroupCount(j.id);
              return (
                <div key={j.id} className={`border rounded-xl p-4 hover:shadow-sm transition-shadow ${isActive(j) ? 'bg-white border-slate-200' : 'bg-slate-50 border-dashed border-slate-300 opacity-60'}`} data-testid={`junior-card-${j.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-slate-900">{j.name}</p>
                      {j.birth_year && <p className="text-xs text-slate-400">{L('Born', 'Jg.')} {j.birth_year}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {!isActive(j) && (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-200 text-slate-600" data-testid={`junior-inactive-badge-${j.id}`}>
                          {L('Inactive', 'Inaktiv')}{j.inactive_since ? ` ${L('since', 'seit')} ${new Date(j.inactive_since).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${badge.cls}`}>{badge.label}</span>
                      {isActive(j) && j.membership_type && (
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${j.membership_paid ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                          data-testid={`junior-paid-badge-${j.id}`}>
                          {j.membership_paid
                            ? `${L('Paid', 'Bezahlt')}${j.membership_paid_at ? ` ${new Date(j.membership_paid_at.length === 10 ? `${j.membership_paid_at}T00:00:00` : j.membership_paid_at).toLocaleDateString('de-CH')}` : ''}`
                            : L('Payment open', 'Zahlung offen')}
                        </span>
                      )}
                    </div>
                  </div>
                  {j.parent_name && <p className="text-xs text-slate-500 mb-1"><UserCircle size={12} className="inline mr-1" />{j.parent_name} {j.parent_phone && `· ${j.parent_phone}`}</p>}
                  {j.training_times && <p className="text-xs text-blue-600 mb-1"><Clock size={12} className="inline mr-1" />{j.training_times}</p>}
                  {j.notes && !(j.notes === j.training_times && /[0-9]{1,2}:[0-9]{2}/.test(j.notes)) && (
                    <p className="text-xs text-emerald-700 mb-1 line-clamp-2" title={j.notes}>
                      <NotePencil size={12} className="inline mr-1" />
                      {j.notes}
                    </p>
                  )}
                  {j.training_frequency && (
                    <p className="text-xs text-purple-600 mb-1 font-semibold" data-testid={`junior-freq-${j.id}`}>
                      {j.training_frequency === '1x_week' ? '1× / Woche'
                        : j.training_frequency === '2x_week' ? '2× / Woche'
                        : j.training_frequency === '3x_week' ? '3× / Woche'
                        : j.training_frequency}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex gap-2 text-xs">
                      <span className="text-slate-400">Level {j.player_level}</span>
                      {groupCount > 0 && <span className="text-blue-600 font-medium">{groupCount} {L('group(s)', 'Gruppe(n)')}</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => toggleJuniorActive(j)} data-testid={`toggle-active-junior-${j.id}`}
                        title={isActive(j) ? L('Set inactive', 'Auf inaktiv setzen') : L('Reactivate', 'Wieder aktivieren')}
                        className={`px-2 py-1 text-[11px] font-bold rounded-lg border ${isActive(j) ? 'text-slate-500 border-slate-200 hover:bg-slate-100' : 'text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100'}`}>
                        {isActive(j) ? L('Deactivate', 'Deaktivieren') : L('Activate', 'Aktivieren')}
                      </button>
                      <button onClick={() => openEditJunior(j)} data-testid={`edit-junior-${j.id}`} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSimple size={14} /></button>
                      <button onClick={() => handleDeleteJunior(j)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredJuniors.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400"><Student size={48} className="mx-auto mb-4 opacity-30" /><p>{L('No juniors yet', 'Noch keine Junioren')}</p></div>
          )}
        </div>
      )}

      {/* GROUPS TAB */}
      {tab === 'groups' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={openAutoGroups}
                className="bg-purple-600 text-white hover:bg-purple-700 font-bold gap-2"
                data-testid="auto-groups-btn">
                <Users size={16} weight="bold" /> {L('Auto-groups from times', 'Auto-Gruppen aus Zeiten')}
              </Button>
              <Button onClick={openMaterializeDialog}
                className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold gap-2"
                data-testid="materialize-btn"
                disabled={groups.length === 0}>
                <CalendarBlank size={16} weight="bold" /> {L('Create weekly bookings', 'Wochenbuchungen erstellen')}
              </Button>
            </div>
            <Button onClick={() => { resetGForm('junior'); setEditingGroup(null); setShowGroupModal(true); }} className="bg-blue-500 text-white hover:bg-blue-600 font-bold gap-2" data-testid="add-group-btn">
              <Plus size={16} weight="bold" /> {L('New Group', 'Neue Gruppe')}
            </Button>
          </div>

          <div className="space-y-6">
            {(() => {
              // Group junior groups by weekday (Mon=0 … Sun=6) and sort each
              // day's groups by start_time so admins can read the week
              // top-to-bottom like a timetable.
              const juniorGroups = groups.filter(g => (g.category || 'junior') === 'junior');
              const byDay = Array.from({ length: 7 }, () => []);
              for (const g of juniorGroups) {
                const wd = Number.isFinite(g.weekday) ? g.weekday : 0;
                if (wd >= 0 && wd < 7) byDay[wd].push(g);
              }
              byDay.forEach(list => list.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));

              return weekdays.map((dayName, wd) => {
                const dayGroups = byDay[wd];
                if (!dayGroups.length) return null; // hide empty weekdays to keep the view tight

                return (
                  <div key={wd} data-testid={`weekday-section-${wd}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-3 border-b-2 border-emerald-200 pb-1.5">
                      <h3 className="text-base font-black uppercase tracking-wider text-emerald-900">
                        {dayName}
                      </h3>
                      <span className="text-xs font-bold text-slate-400 tabular-nums">
                        {dayGroups.length} {dayGroups.length === 1 ? L('group', 'Gruppe') : L('groups', 'Gruppen')}
                      </span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {dayGroups.map(g => {
              const isBooked = g.bookings_count > 0;
              const saveInline = async (patch) => {
                try {
                  const payload = {
                    name: g.name, weekday: g.weekday, start_time: g.start_time,
                    duration_minutes: g.duration_minutes, junior_ids: g.junior_ids || [],
                    trainer_id: g.trainer_id || null, court_id: g.court_id || null,
                    price_per_junior: g.price_per_junior || 0, notes: g.notes || null,
                    ...patch,
                  };
                  await API.put(`/academy/groups/${g.id}`, payload);
                  toast.success(L('Updated', 'Aktualisiert'));
                  loadData();
                } catch (e) { toast.error(L('Error', 'Fehler')); }
              };
              const bookThisGroup = () => {
                if (!g.trainer_id) {
                  toast.error(L('Assign a trainer first', 'Bitte zuerst Trainer zuweisen'));
                  return;
                }
                if (!(g.junior_ids || []).length && !(g.member_ids || []).length) {
                  toast.error(L('No participants in this group yet', 'Noch keine Teilnehmer in dieser Gruppe'));
                  return;
                }
                // Default range: today → today + 8 weeks. Admin can edit before confirming.
                const today = new Date();
                const inEightWeeks = new Date(today.getTime() + 56 * 24 * 60 * 60 * 1000);
                const iso = (d) => d.toISOString().slice(0, 10);
                setBookGroupDialog({
                  group: g,
                  startDate: iso(today),
                  endDate: iso(inEightWeeks),
                  loading: false,
                });
              };
              return (
              <div key={g.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`group-card-${g.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-slate-900 text-lg">{g.name}</p>
                    <p className="text-sm text-slate-500">
                      {weekdays[g.weekday]} · {g.start_time} · {g.duration_minutes} min
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Button
                      onClick={bookThisGroup}
                      size="sm"
                      className="h-7 px-2 text-xs bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
                      data-testid={`book-group-${g.id}`}
                      title={L('Create weekly bookings on the calendar for this group', 'Wochenbuchungen für diese Gruppe im Kalender erstellen')}
                    >
                      <CalendarBlank size={12} weight="bold" /> {L('Book weeks', 'Wochen buchen')}
                    </Button>
                    <button onClick={() => openEditGroup(g)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><PencilSimple size={16} /></button>
                    <button onClick={() => handleDeleteGroup(g)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash size={16} /></button>
                  </div>
                </div>
                {/* Booking status */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {isBooked ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800" data-testid={`group-booked-badge-${g.id}`}>
                      ✓ {L(`Booked (${g.bookings_count})`, `Gebucht (${g.bookings_count})`)}
                      {g.last_booking_date && <span className="text-emerald-600 font-medium ml-1">{L('until', 'bis')} {g.last_booking_date.slice(-5).replace('-','.')}</span>}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800" data-testid={`group-unbooked-badge-${g.id}`}>
                      ⚠ {L('Not yet booked', 'Noch nicht gebucht')}
                    </span>
                  )}
                  {!g.trainer_id && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-800">
                      {L('No trainer', 'Kein Trainer')}
                    </span>
                  )}
                </div>
                {/* Inline quick-assignment — always available so admins can change trainer/court even after bookings exist */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 mb-3 space-y-2" data-testid={`inline-assign-${g.id}`}>
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={g.trainer_id || '_none'} onValueChange={(v) => saveInline({ trainer_id: v === '_none' ? null : v })}>
                      <SelectTrigger className="bg-white border-slate-300 h-8 text-xs" data-testid={`group-trainer-${g.id}`}>
                        <SelectValue placeholder={L('Trainer', 'Trainer')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="_none">{L('— no trainer —', '— kein Trainer —')}</SelectItem>
                        {trainers.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={g.court_id || '_none'} onValueChange={(v) => saveInline({ court_id: v === '_none' ? null : v })}>
                      <SelectTrigger className="bg-white border-slate-300 h-8 text-xs" data-testid={`group-court-${g.id}`}>
                        <SelectValue placeholder={L('Court', 'Court')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="_none">{L('— auto —', '— auto —')}</SelectItem>
                        {courts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" min="0" step="5"
                      defaultValue={g.price_per_junior || 0}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        if (v !== (g.price_per_junior || 0)) saveInline({ price_per_junior: v });
                      }}
                      className="bg-white border-slate-300 h-8 text-xs"
                      placeholder={L('Price/Jun.', 'Preis/Jun.')}
                      data-testid={`group-price-${g.id}`}
                    />
                  </div>
                  {isBooked && (
                    <p className="text-[10px] text-slate-500 italic">
                      {L('Changes affect future bookings only. Re-run "Create weekly bookings" to add missing lessons.', 'Änderungen wirken auf zukünftige Buchungen. "Wochenbuchungen erstellen" erneut ausführen, um fehlende Lektionen zu ergänzen.')}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(g.junior_names || []).map((name, i) => (
                    <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg">{name}</span>
                  ))}
                  {(!g.junior_names || g.junior_names.length === 0) && <span className="text-xs text-slate-400">{L('No juniors assigned', 'Keine Junioren zugewiesen')}</span>}
                </div>
                {g.notes && <p className="text-xs text-slate-400 mt-2 italic">{g.notes}</p>}
              </div>
              );
            })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          {groups.filter(g => (g.category || 'junior') === 'junior').length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400"><CalendarBlank size={48} className="mx-auto mb-4 opacity-30" /><p>{L('No groups yet', 'Noch keine Gruppen')}</p></div>
          )}
        </div>
      )}

      {/* ADULT ACADEMY TAB — removed; adult groups are now handled via
          the "Group Trainings" page which supports online signup, waitlists
          and per-user vouchers. */}

      {/* Junior Modal */}
      <Dialog open={showJuniorModal} onOpenChange={setShowJuniorModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="junior-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingJunior ? L('Edit Junior', 'Junior bearbeiten') : L('Add Junior', 'Junior hinzufügen')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2"><Label className="label-text">{L('Name', 'Name')} *</Label><Input value={jForm.name} onChange={(e) => setJForm({...jForm, name: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="junior-name" /></div>
              <div className="space-y-1"><Label className="label-text">{L('Birth Year', 'Jahrgang')}</Label><Input type="number" value={jForm.birth_year} onChange={(e) => setJForm({...jForm, birth_year: e.target.value})} className="bg-slate-50 border-slate-200" placeholder="2015" data-testid="junior-birth-year" /></div>
              <div className="space-y-1"><Label className="label-text">{L('Level', 'Level')}</Label>
                <Select value={jForm.player_level} onValueChange={(v) => setJForm({...jForm, player_level: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{[1,2,3,4,5,6,7].map(l => <SelectItem key={l} value={l.toString()}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-400 uppercase font-semibold mb-2">{L('Parent / Contact', 'Eltern / Kontakt')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2"><Label className="label-text">{L('Parent Name', 'Elternname')}</Label><Input value={jForm.parent_name} onChange={(e) => setJForm({...jForm, parent_name: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="junior-parent-name" /></div>
                <div className="space-y-1"><Label className="label-text">{L('Phone', 'Telefon')}</Label><Input value={jForm.parent_phone} onChange={(e) => setJForm({...jForm, parent_phone: e.target.value})} className="bg-slate-50 border-slate-200" data-testid="junior-parent-phone" /></div>
                <div className="space-y-1"><Label className="label-text">{L('Email', 'E-Mail')}</Label><Input value={jForm.parent_email} onChange={(e) => setJForm({...jForm, parent_email: e.target.value})} className="bg-slate-50 border-slate-200" /></div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-400 uppercase font-semibold mb-2">{L('Membership', 'Abo')}</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  {v:'', l: L('None','Kein Abo')},
                  {v:'three_month', l: L('3 months','3 Monate')},
                  {v:'full_year', l: L('Yearly','Jahresabo')},
                ].map(({v, l}) => (
                  <button key={v} type="button" onClick={() => {
                    // Auto-calc end date when a type is chosen: if start is
                    // already set, apply +3 months or +12 months; else leave
                    // the admin to enter start manually first.
                    const nextForm = { ...jForm, membership_type: v, membership_paid: v === jForm.membership_type ? jForm.membership_paid : false };
                    if (v && jForm.membership_start) {
                      const d = new Date(jForm.membership_start);
                      if (!isNaN(d)) {
                        if (v === 'three_month') d.setMonth(d.getMonth() + 3);
                        else if (v === 'full_year') d.setFullYear(d.getFullYear() + 1);
                        nextForm.membership_end = d.toISOString().slice(0, 10);
                      }
                    } else if (!v) {
                      nextForm.membership_end = '';
                    }
                    setJForm(nextForm);
                  }}
                    data-testid={`junior-membership-type-${v || 'none'}`}
                    className={`p-2 rounded-lg text-xs font-bold border-2 transition-all ${jForm.membership_type === v ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'}`}>
                    {l}
                  </button>
                ))}
              </div>
              {jForm.membership_type && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="label-text">{L('Start', 'Startdatum')}</Label>
                    <Input type="date" value={jForm.membership_start} onChange={(e) => {
                      // On start date change: auto-calc end date from the
                      // currently selected type.
                      const start = e.target.value;
                      const next = { ...jForm, membership_start: start, membership_paid: false };
                      if (start && jForm.membership_type) {
                        const d = new Date(start);
                        if (!isNaN(d)) {
                          if (jForm.membership_type === 'three_month') d.setMonth(d.getMonth() + 3);
                          else if (jForm.membership_type === 'full_year') d.setFullYear(d.getFullYear() + 1);
                          next.membership_end = d.toISOString().slice(0, 10);
                        }
                      }
                      setJForm(next);
                    }} className="bg-slate-50 border-slate-200" data-testid="junior-membership-start" />
                  </div>
                  <div className="space-y-1">
                    <Label className="label-text">{L('End (auto)', 'Ende (auto)')}</Label>
                    <Input type="date" value={jForm.membership_end} onChange={(e) => setJForm({...jForm, membership_end: e.target.value, membership_paid: false})} className="bg-slate-50 border-slate-200" data-testid="junior-membership-end" />
                  </div>
                </div>
              )}
              {jForm.membership_type && user?.role === 'admin' && (
                <div className={`mt-3 flex flex-wrap items-center gap-3 p-3 rounded-lg border-2 transition-colors ${jForm.membership_paid ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white'}`}
                  data-testid="junior-membership-paid">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox checked={!!jForm.membership_paid} data-testid="junior-membership-paid-checkbox"
                      onCheckedChange={(v) => setJForm({ ...jForm, membership_paid: !!v, membership_paid_date: v ? (jForm.membership_paid_date || new Date().toISOString().slice(0, 10)) : '' })} />
                    <span className="text-sm font-bold text-slate-800">{L('Membership paid', 'Abo bezahlt')}</span>
                  </label>
                  {jForm.membership_paid && (
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-slate-500">{L('Paid on', 'Zahldatum')}</span>
                      <Input type="date" value={jForm.membership_paid_date || ''} max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setJForm({ ...jForm, membership_paid_date: e.target.value })}
                        className="h-8 w-[150px] bg-white border-slate-200 text-sm" data-testid="junior-membership-paid-date" />
                    </div>
                  )}
                  {jForm.membership_paid && editingJunior?.membership_paid_by_name && (
                    <span className="w-full text-right text-[11px] text-slate-500">{L('by', 'erfasst von')} {editingJunior.membership_paid_by_name}</span>
                  )}
                </div>
              )}
              {jForm.membership_type && user?.role === 'admin' && editingJunior && !editingJunior.membership_paid && !jForm.membership_paid && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-500" data-testid="junior-reminder-info">
                    {editingJunior.payment_reminder_sent_at
                      ? L(`Last reminder ${new Date(editingJunior.payment_reminder_sent_at).toLocaleDateString('de-CH')}`, `Letzte Erinnerung ${new Date(editingJunior.payment_reminder_sent_at).toLocaleDateString('de-CH')}`)
                      : (editingJunior.parent_email ? '' : L('No parent e-mail stored', 'Keine Eltern-E-Mail hinterlegt'))}
                  </span>
                  <Button type="button" variant="outline" size="sm" disabled={!editingJunior.parent_email || reminding}
                    onClick={() => sendReminder(editingJunior)} className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" data-testid="junior-send-reminder-btn">
                    <Bell size={14} className="mr-1" /> {L('Send payment reminder', 'Zahlungserinnerung senden')}
                  </Button>
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-400 uppercase font-semibold mb-2">{L('Training Frequency', 'Trainingsfrequenz')}</p>
              <div className="grid grid-cols-4 gap-2">
                {[{v:'', l: L('None','Kein')}, {v:'1x_week', l: '1× / Woche'}, {v:'2x_week', l: '2× / Woche'}, {v:'3x_week', l: '3× / Woche'}].map(({v, l}) => (
                  <button key={v} type="button" onClick={() => setJForm({...jForm, training_frequency: v})}
                    data-testid={`junior-frequency-${v || 'none'}`}
                    className={`p-2 rounded-lg text-xs font-bold border-2 transition-all ${jForm.training_frequency === v ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1"><Label className="label-text">{L('Training Times', 'Trainingszeiten')}</Label><Input value={jForm.training_times} onChange={(e) => setJForm({...jForm, training_times: e.target.value})} className="bg-slate-50 border-slate-200" placeholder={L('e.g. Monday 17:00-18:00; Friday 16:00-17:00', 'z.B. Montag 17:00-18:00; Freitag 16:00-17:00')} data-testid="junior-training-times" /></div>
            <div className="space-y-1">
              <Label className="label-text">{L('Coach Notes', 'Coach-Notizen')}</Label>
              <textarea
                value={jForm.notes}
                onChange={(e) => setJForm({...jForm, notes: e.target.value})}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-200"
                placeholder={L('Progress, injuries, special needs — visible to all coaches, admin and reception.',
                               'Fortschritt, Verletzungen, Besonderheiten — sichtbar für alle Coaches, Admin und Empfang.')}
                data-testid="junior-notes"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowJuniorModal(false)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={handleSaveJunior} disabled={!jForm.name} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-junior-btn">{L('Save', 'Speichern')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingGroup
                ? (gForm.category === 'adult' ? L('Edit Adult Group', 'Erwachsenen-Gruppe bearbeiten') : L('Edit Group', 'Gruppe bearbeiten'))
                : (gForm.category === 'adult' ? L('New Adult Group', 'Neue Erwachsenen-Gruppe') : L('New Training Group', 'Neue Trainingsgruppe'))}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-1"><Label className="label-text">{L('Group Name', 'Gruppenname')} *</Label><Input value={gForm.name} onChange={(e) => setGForm({...gForm, name: e.target.value})} className="bg-slate-50 border-slate-200" placeholder={L('e.g. Monday U12', 'z.B. Montag U12')} data-testid="group-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="label-text">{L('Trainer', 'Trainer')}</Label>
                <Select value={gForm.trainer_id || 'none'} onValueChange={(v) => setGForm({...gForm, trainer_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="label-text">{L('Court', 'Platz')}</Label>
                <Select value={gForm.court_id || 'none'} onValueChange={(v) => setGForm({...gForm, court_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{courts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="label-text">{L('Day', 'Tag')}</Label>
                <Select value={gForm.weekday} onValueChange={(v) => setGForm({...gForm, weekday: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{weekdays.map((d, i) => <SelectItem key={i} value={i.toString()}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="label-text">{L('Time', 'Zeit')}</Label>
                <Select value={gForm.start_time} onValueChange={(v) => setGForm({...gForm, start_time: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-48">{Array.from({length: 30}, (_, i) => { const h = Math.floor(i/2)+7; const m = i%2===0?'00':'30'; return `${String(h).padStart(2,'0')}:${m}`; }).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="label-text">{L('Duration', 'Dauer')}</Label>
                <Select value={gForm.duration_minutes} onValueChange={(v) => setGForm({...gForm, duration_minutes: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200"><SelectItem value="60">60 min</SelectItem><SelectItem value="90">90 min</SelectItem><SelectItem value="120">120 min</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {/* Member Selection — dynamic based on category */}
            {gForm.category === 'adult' ? (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-2">
                  {L('Members in this group', 'Mitglieder in dieser Gruppe')} ({gForm.member_ids.length})
                </p>
                <Input
                  value={adultSearch}
                  onChange={(e) => setAdultSearch(e.target.value)}
                  placeholder={L('Search members by name or email…', 'Mitglieder nach Name oder E-Mail suchen…')}
                  className="bg-slate-50 border-slate-200 mb-2"
                  data-testid="adult-group-member-search"
                />
                {gForm.member_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {gForm.member_ids.map(mid => {
                      const m = adultMembers.find(x => x.id === mid);
                      const name = m?.name || editingGroup?.member_names?.[(editingGroup?.member_ids || []).indexOf(mid)] || '…';
                      return (
                        <span key={mid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 border border-purple-200 text-purple-700 font-semibold">
                          {name}
                          <button onClick={() => toggleMemberInGroup(mid)} className="text-purple-400 hover:text-red-500"><X size={10} /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="max-h-48 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2">
                  {adultMembers.map(m => {
                    const selected = gForm.member_ids.includes(m.id);
                    return (
                      <button key={m.id} onClick={() => toggleMemberInGroup(m.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${selected ? 'bg-purple-50 border border-purple-300 text-purple-700 font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}
                        data-testid={`group-member-${m.id}`}>
                        <span className="truncate">
                          {m.name}
                          {m.is_member && <span className="ml-1 text-sky-500">✓</span>}
                          <span className="text-slate-400 ml-1 text-xs">· Lv {m.player_level || '?'}</span>
                        </span>
                        {selected && <span className="text-purple-500 text-xs font-bold">&#10003;</span>}
                      </button>
                    );
                  })}
                  {adultMembers.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">
                      {adultSearch ? L('No match', 'Keine Treffer') : L('Type to search…', 'Tippen zum Suchen…')}
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  <Label className="label-text">{L('Price per Member (CHF)', 'Preis pro Mitglied (CHF)')}</Label>
                  <Input type="number" step="5" min="0" value={gForm.price_per_member} onChange={(e) => setGForm({...gForm, price_per_member: e.target.value})} className="bg-slate-50 border-slate-200 mt-1" placeholder="0" data-testid="group-price-per-member" />
                  {autoAdultPrice && (
                    <p className="text-[11px] text-purple-600 mt-1.5 flex items-center gap-1" data-testid="adult-price-auto-hint">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold uppercase tracking-wide text-[10px]">
                        {autoAdultPrice.tier === 'peak' ? L('Peak', 'Peak') : L('Off-Peak', 'Off-Peak')}
                      </span>
                      <span>
                        {L(
                          `Auto-priced: ${autoAdultPrice.session_weeks}-week session, ${autoAdultPrice.price_per_member} CHF per member.`,
                          `Automatisch: ${autoAdultPrice.session_weeks}-Wochen-Session, ${autoAdultPrice.price_per_member} CHF pro Mitglied.`
                        )}
                      </span>
                    </p>
                  )}
                </div>

                {/* Auto-reservation — only when creating (not editing) */}
                {!editingGroup && (
                  <div className="mt-4 p-3 rounded-xl bg-purple-50 border border-purple-200" data-testid="adult-session-reservation">
                    <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">
                      {L('Auto-reserve courts', 'Plätze automatisch reservieren')}
                    </p>
                    <p className="text-[11px] text-purple-600 mb-3">
                      {L(
                        'Court will be reserved weekly on the selected day and time.',
                        'Der Platz wird wöchentlich am gewählten Tag und zur gewählten Uhrzeit reserviert.'
                      )}
                    </p>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {['6', '12', '0'].map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setGForm({...gForm, session_weeks: v})}
                          className={`px-2 py-2 rounded-lg text-xs font-bold transition-colors ${gForm.session_weeks === v ? 'bg-purple-600 text-white' : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-100'}`}
                          data-testid={`session-weeks-${v}`}
                        >
                          {v === '0' ? L('None', 'Keine') : `${v} ${L('weeks', 'Wochen')}`}
                        </button>
                      ))}
                    </div>
                    {gForm.session_weeks !== '0' && (
                      <>
                        <Label className="label-text text-purple-700">{L('Start date (optional)', 'Startdatum (optional)')}</Label>
                        <Input
                          type="date"
                          value={gForm.session_start_date}
                          onChange={(e) => setGForm({...gForm, session_start_date: e.target.value})}
                          className="bg-white border-purple-200 mt-1"
                          data-testid="session-start-date"
                        />
                        <p className="text-[11px] text-purple-500 mt-1.5">
                          {L(
                            `Leave empty to start next ${weekdays[parseInt(gForm.weekday)]}.`,
                            `Leer lassen, um nächsten ${weekdays[parseInt(gForm.weekday)]} zu starten.`
                          )}
                        </p>
                      </>
                    )}
                    {gForm.session_weeks !== '0' && (!gForm.trainer_id || !gForm.court_id || gForm.member_ids.length === 0) && (
                      <p className="text-[11px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="session-incomplete-warning">
                        ⚠ {L('Trainer, court and at least one member are required to auto-reserve.', 'Trainer, Platz und mindestens ein Mitglied werden zur Reservierung benötigt.')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs text-slate-400 uppercase font-semibold mb-2">{L('Juniors in this group', 'Junioren in dieser Gruppe')} ({gForm.junior_ids.length})</p>
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-slate-200 rounded-xl p-2">
                    {juniors.filter(j => isActive(j) || gForm.junior_ids.includes(j.id)).map(j => {
                      const selected = gForm.junior_ids.includes(j.id);
                      return (
                        <button key={j.id} onClick={() => toggleJuniorInGroup(j.id)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${selected ? 'bg-emerald-50 border border-emerald-300 text-emerald-700 font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}
                          data-testid={`group-junior-${j.id}`}>
                          <span>{j.name} {j.birth_year ? `(${j.birth_year})` : ''}</span>
                          {selected && <span className="text-emerald-500 text-xs font-bold">&#10003;</span>}
                        </button>
                      );
                    })}
                    {juniors.length === 0 && <p className="text-xs text-slate-400 text-center py-4">{L('Add juniors first', 'Zuerst Junioren hinzufügen')}</p>}
                  </div>
                </div>

                <div className="space-y-1"><Label className="label-text">{L('Price per Junior (CHF)', 'Preis pro Junior (CHF)')}</Label><Input type="number" step="5" min="0" value={gForm.price_per_junior} onChange={(e) => setGForm({...gForm, price_per_junior: e.target.value})} className="bg-slate-50 border-slate-200" placeholder="0" data-testid="group-price-per-junior" /></div>
              </>
            )}
            <div className="space-y-1"><Label className="label-text">{L('Notes', 'Notizen')}</Label><Input value={gForm.notes} onChange={(e) => setGForm({...gForm, notes: e.target.value})} className="bg-slate-50 border-slate-200" /></div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowGroupModal(false)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={handleSaveGroup} disabled={!gForm.name} className="flex-1 bg-blue-500 text-white hover:bg-blue-600 font-bold" data-testid="save-group-btn">{L('Save', 'Speichern')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Groups Dialog */}
      <Dialog open={showAutoGroups} onOpenChange={(open) => { if (!open) { setShowAutoGroups(false); setAgResult(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="auto-groups-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Auto-generate Groups from Training Times', 'Gruppen aus Trainingszeiten generieren')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Scans every junior\'s training times and clusters them into groups (weekday + time). Existing groups are extended.', 'Liest bei jedem Junior die Trainingszeiten und gruppiert sie (Wochentag + Zeit). Bestehende Gruppen werden erweitert.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {/* Level toggle */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">{L('Group by player level', 'Nach Level gruppieren')}</p>
                <p className="text-[11px] text-slate-500">{L('Juniors at the same time but different levels go into separate groups.', 'Junioren mit gleicher Zeit aber unterschiedlichem Level kommen in getrennte Gruppen.')}</p>
              </div>
              <button onClick={() => setAgByLevel(v => !v)}
                className={`w-12 h-6 rounded-full transition-colors ${agByLevel ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid="auto-groups-by-level">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${agByLevel ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="label-text">{L('Default Trainer', 'Standard-Trainer')}</Label>
                <Select value={agDefaultTrainer || '_none'} onValueChange={(v) => setAgDefaultTrainer(v === '_none' ? '' : v)}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-xs" data-testid="auto-groups-trainer"><SelectValue placeholder={L('None', 'Kein')} /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="_none">{L('None (assign later)', 'Kein (später zuweisen)')}</SelectItem>
                    {trainers.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Default Court', 'Standard-Court')}</Label>
                <Select value={agDefaultCourt || '_none'} onValueChange={(v) => setAgDefaultCourt(v === '_none' ? '' : v)}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-xs" data-testid="auto-groups-court"><SelectValue placeholder={L('None', 'Kein')} /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="_none">{L('None (assign later)', 'Kein (später zuweisen)')}</SelectItem>
                    {courts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('Price/Junior', 'Preis/Junior')}</Label>
                <Input type="number" min="0" step="5" value={agDefaultPrice} onChange={(e) => setAgDefaultPrice(e.target.value)} className="bg-slate-50 border-slate-200" data-testid="auto-groups-price" />
              </div>
            </div>

            {agPreview && !agResult && (() => {
              const newCount = agPreview.groups.filter(g => !g.already_exists).length;
              const existingCount = agPreview.groups.filter(g => g.already_exists).length;
              return (
                <div className="space-y-2">
                  {newCount > 0 ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm font-semibold text-emerald-800" data-testid="auto-groups-total">
                      ✓ {L(`${newCount} new groups will be created`, `${newCount} neue Gruppen werden erstellt`)}
                      {existingCount > 0 && <span className="text-xs font-normal text-slate-500 ml-2">({existingCount} {L('already exist — skipped', 'bereits vorhanden – werden übersprungen')})</span>}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900" data-testid="auto-groups-total">
                      <p className="font-semibold mb-1">{L('Nothing to do — all groups already exist', 'Nichts zu tun — alle Gruppen existieren bereits')}</p>
                      <p className="text-xs text-amber-700">{L(`All ${agPreview.total_groups} time-slots from junior profiles are already mapped to groups. Edit junior training_times to detect new groups.`, `Alle ${agPreview.total_groups} Zeitslots aus den Junior-Profilen sind bereits als Gruppen vorhanden. Trainingszeiten bei Junioren ändern, damit neue Gruppen erkannt werden.`)}</p>
                    </div>
                  )}
                  <div className="space-y-1.5 max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-2">
                    {agPreview.groups.map((g, i) => (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${g.already_exists ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-800'}`}>
                        <span className="font-semibold shrink-0 w-24">{g.weekday_name}</span>
                        <span className="shrink-0 font-mono">{g.start_time}–{g.end_time}</span>
                        <span className="text-xs text-slate-500 shrink-0">{g.duration_minutes} min</span>
                        {g.level !== null && g.level !== undefined && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold shrink-0">L{g.level}</span>
                        )}
                        <span className="ml-auto shrink-0 font-bold">{g.junior_count} {L('juniors', 'Jun.')}</span>
                        {g.already_exists && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-300 text-slate-700 font-bold shrink-0">{L('exists', 'vorhanden')}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {agResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-1">
                <p className="font-bold text-emerald-800">✓ {agResult.created} {L('new groups created', 'neue Gruppen erstellt')}</p>
                {agResult.extended > 0 && <p className="text-sm text-slate-700">↪ {agResult.extended} {L('existing groups extended with new juniors', 'bestehende Gruppen erweitert')}</p>}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAutoGroups(false)} className="flex-1 border-slate-200 text-slate-600" data-testid="auto-groups-cancel">
                {agResult ? L('Close', 'Schliessen') : L('Cancel', 'Abbrechen')}
              </Button>
              {!agResult && (() => {
                const newCount = agPreview ? agPreview.groups.filter(g => !g.already_exists).length : 0;
                return (
                  <Button onClick={runAutoGroups} disabled={agLoading || !agPreview || newCount === 0}
                    className="flex-1 bg-purple-600 text-white hover:bg-purple-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="auto-groups-confirm">
                    {agLoading ? '...' : (newCount === 0 ? L('Nothing to create', 'Nichts zu erstellen') : L(`Create ${newCount} groups`, `${newCount} Gruppen erstellen`))}
                  </Button>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Materialize Bookings Dialog */}
      <Dialog open={showMaterialize} onOpenChange={(open) => { if (!open) { setShowMaterialize(false); setMResult(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="materialize-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Create Weekly Bookings', 'Wochenbuchungen erstellen')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Creates training bookings for every group, every week, in the admin grid. Max 4 juniors per booking.', 'Erstellt Trainings-Buchungen für jede Gruppe, jede Woche, im Admin-Grid. Max. 4 Junioren pro Buchung.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-text">{L('Start Date', 'Startdatum')}</Label>
                <Input type="date" value={mStartDate} onChange={(e) => setMStartDate(e.target.value)} className="bg-slate-50 border-slate-200" data-testid="materialize-start-date" />
              </div>
              <div className="space-y-1">
                <Label className="label-text">{L('End Date', 'Enddatum')}</Label>
                <Input type="date" value={mEndDate} onChange={(e) => setMEndDate(e.target.value)} className="bg-slate-50 border-slate-200" data-testid="materialize-end-date" />
              </div>
            </div>
            <Button onClick={loadMaterializePreview} disabled={mLoading} variant="outline" className="w-full border-slate-300 text-slate-700 font-semibold" data-testid="refresh-preview-btn">
              {mLoading ? '...' : L('Refresh Preview', 'Vorschau aktualisieren')}
            </Button>

            {mPreview && !mResult && (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm font-semibold text-emerald-800" data-testid="materialize-total">
                  {L(`${mPreview.total_bookings} bookings will be created across ${mPreview.groups.length} groups`, `${mPreview.total_bookings} Buchungen in ${mPreview.groups.length} Gruppen werden erstellt`)}
                </div>
                {mPreview.groups.map(g => (
                  <div key={g.group_id} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`materialize-group-${g.group_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-slate-800">{g.group_name}</p>
                        <p className="text-xs text-slate-500">
                          {weekdays[g.weekday]} · {g.start_time} · {g.weeks_count} {L('weeks', 'Wochen')} · {g.total_juniors} {L('juniors', 'Junioren')}
                        </p>
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {g.price_per_junior > 0 ? `${g.price_per_junior.toFixed(0)} CHF/Jun.` : L('Free', 'Gratis')}
                      </span>
                    </div>
                    {g.chunks.map(c => {
                      const key = `${g.group_id}:${c.chunk_index}`;
                      const isMain = c.chunk_index === 0;
                      return (
                        <div key={c.chunk_index} className={`flex items-center gap-2 flex-wrap p-2 rounded-lg ${isMain ? 'bg-slate-50' : 'bg-amber-50 border border-amber-200'}`}>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-extrabold ${isMain ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                            {isMain ? L('MAIN', 'HAUPT') : L(`OVERFLOW ${c.chunk_index}`, `OVERFLOW ${c.chunk_index}`)}
                          </span>
                          <span className="text-xs text-slate-700 font-semibold">{c.size} {L('juniors', 'Jun.')}</span>
                          {isMain ? (
                            <span className="text-xs text-purple-700">→ {c.default_trainer_name || L('(no trainer)', '(kein Trainer)')}</span>
                          ) : (
                            <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                              <span className="text-xs text-slate-500 shrink-0">{L('Trainer', 'Trainer')}:</span>
                              <Select value={mOverflow[key] || ''} onValueChange={(v) => setMOverflow(prev => ({ ...prev, [key]: v }))}>
                                <SelectTrigger className="bg-white border-amber-300 h-8 text-xs" data-testid={`overflow-trainer-${g.group_id}-${c.chunk_index}`}>
                                  <SelectValue placeholder={L('Select...', 'Wählen...')} />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                  {mPreview.available_trainers
                                    .filter(t => t.id !== g.trainer_id)
                                    .map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)
                                  }
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {mPreview.groups.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6">{L('No groups with juniors found', 'Keine Gruppen mit Junioren gefunden')}</p>
                )}
              </div>
            )}

            {mResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-1" data-testid="materialize-result">
                <p className="font-bold text-emerald-800">✓ {mResult.created} {L('bookings created', 'Buchungen erstellt')}</p>
                {mResult.skipped_existing > 0 && <p className="text-xs text-slate-600">↪ {mResult.skipped_existing} {L('already existed (skipped)', 'bereits vorhanden (übersprungen)')}</p>}
                {mResult.skipped_conflict > 0 && <p className="text-xs text-amber-700">⚠ {mResult.skipped_conflict} {L('conflicts with other bookings', 'Konflikte mit anderen Buchungen')}</p>}
                {mResult.missing_court.length > 0 && <p className="text-xs text-red-700">⚠ {mResult.missing_court.length} {L('slots had no free court for overflow group', 'Slots ohne freien Court für Overflow-Gruppe')}</p>}
                {mResult.errors.length > 0 && <p className="text-xs text-red-700">✗ {mResult.errors.length} {L('errors', 'Fehler')}</p>}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowMaterialize(false)} className="flex-1 border-slate-200 text-slate-600" data-testid="materialize-cancel">
                {mResult ? L('Close', 'Schliessen') : L('Cancel', 'Abbrechen')}
              </Button>
              {!mResult && (
                <Button onClick={runMaterialize} disabled={mLoading || !mPreview} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 font-bold" data-testid="materialize-confirm">
                  {mLoading ? '...' : L('Create Bookings', 'Buchungen anlegen')}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader><DialogTitle className="text-lg font-bold">{L('Confirm', 'Bestätigen')}</DialogTitle></DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={() => confirmAction?.onConfirm()} data-testid="confirm-action-btn" className={`flex-1 text-white font-bold ${confirmAction?.label ? 'bg-slate-700 hover:bg-slate-800' : 'bg-red-500 hover:bg-red-600'}`}>{confirmAction?.label || L('Delete', 'Löschen')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-group "Wochen buchen" confirm dialog. Explains exactly what
          will happen before hitting the materialize endpoint. */}
      <Dialog open={!!bookGroupDialog} onOpenChange={(open) => { if (!open) setBookGroupDialog(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="book-group-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {L('Book weekly sessions', 'Wochenbuchungen erstellen')}
            </DialogTitle>
          </DialogHeader>
          {bookGroupDialog && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm space-y-1">
                <p className="font-bold text-emerald-900">{bookGroupDialog.group.name}</p>
                <p className="text-emerald-800">
                  {weekdays[bookGroupDialog.group.weekday]} · {bookGroupDialog.group.start_time} · {bookGroupDialog.group.duration_minutes} min
                </p>
                <p className="text-emerald-700 text-xs">
                  {(bookGroupDialog.group.junior_ids || bookGroupDialog.group.member_ids || []).length} {L('participants', 'Teilnehmer')}
                  {bookGroupDialog.group.court_id && ` · ${L('court:', 'Court:')} ${courts.find(c => c.id === bookGroupDialog.group.court_id)?.name || bookGroupDialog.group.court_id}`}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-text">{L('From', 'Von')}</Label>
                  <Input
                    type="date"
                    value={bookGroupDialog.startDate}
                    onChange={(e) => setBookGroupDialog(d => ({ ...d, startDate: e.target.value }))}
                    className="bg-slate-50 border-slate-200"
                    data-testid="book-group-start-date"
                  />
                </div>
                <div>
                  <Label className="label-text">{L('Until', 'Bis')}</Label>
                  <Input
                    type="date"
                    value={bookGroupDialog.endDate}
                    onChange={(e) => setBookGroupDialog(d => ({ ...d, endDate: e.target.value }))}
                    className="bg-slate-50 border-slate-200"
                    data-testid="book-group-end-date"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                {L(
                  'Creates one training booking per week on the group\'s weekday. Existing bookings are skipped. If no court is free at that time, the session will not be booked and you\'ll get a clean error message.',
                  'Erstellt pro Woche eine Trainings-Buchung am Wochentag der Gruppe. Bereits vorhandene Buchungen werden übersprungen. Wenn kein Court zur Zeit frei ist, wird nichts gebucht und du bekommst eine klare Fehlermeldung.'
                )}
              </p>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setBookGroupDialog(null)}
                  className="flex-1 border-slate-200 text-slate-600"
                  disabled={bookGroupDialog.loading}
                  data-testid="book-group-cancel"
                >
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={async () => {
                    if (!bookGroupDialog.startDate || !bookGroupDialog.endDate) {
                      toast.error(L('Pick a date range', 'Bitte Datumsbereich wählen'));
                      return;
                    }
                    if (bookGroupDialog.endDate < bookGroupDialog.startDate) {
                      toast.error(L('End date must be after start date', 'Enddatum muss nach dem Startdatum liegen'));
                      return;
                    }
                    setBookGroupDialog(d => ({ ...d, loading: true }));
                    try {
                      const { data } = await API.post('/academy/materialize', {
                        start_date: bookGroupDialog.startDate,
                        end_date: bookGroupDialog.endDate,
                        only_group_id: bookGroupDialog.group.id,
                        overflow_trainers: [],
                      });
                      surfaceMaterializeResult(data);
                      // Only close on success (created > 0) so the admin can adjust the range and retry.
                      if ((data.created || 0) > 0) {
                        setBookGroupDialog(null);
                        loadData();
                      } else {
                        setBookGroupDialog(d => ({ ...d, loading: false }));
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
                      setBookGroupDialog(d => ({ ...d, loading: false }));
                    }
                  }}
                  disabled={bookGroupDialog.loading}
                  className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
                  data-testid="book-group-confirm"
                >
                  {bookGroupDialog.loading ? L('Booking…', 'Buche…') : L('Book weeks', 'Wochen buchen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAcademy;
